import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { InputCommonProps, ValidatedInputProps } from "./Common";
import commonStyles from "./Common.module.scss";
import styles from "./DateSelect.module.scss";
import { useForceUpdate } from "../../Hooks";
import { classNames } from "../../Utilities";
import { Select } from "./Select";

interface DateSelectProps{
    minYear?: number;
    maxYear?: number;
    yearLabel?: string;
    monthLabel?: string;
    dayLabel?: string;
    value: string;
    onChange: (newValue: string) => void;
}

export type CombinedDateSelectProps = DateSelectProps & ValidatedInputProps & InputCommonProps;

export const DateSelect: FC<CombinedDateSelectProps> = (props) => {
    const forceUpdate = useForceUpdate();

    useEffect(() => {
        props.validator?.register({
            key: props.id,
            validations: props.validations,
            value: props.value?.toString(),
            forceUpdate: forceUpdate,
            validationMessages: props.customMessages
        });

        return () => {
            props.validator?.unregister(props.id);
        };
    }, []);

    useEffect(() => {
        if (props.validator) {
            props.validator.setValue(props.id, props.value?.toString() ?? null);
            forceUpdate();
        }
    }, [props.value]);

    const isErrorState = (): boolean => {
        return !!props.validator && props.validator.submitAttempted() && props.validator.getErrors(props.id).length > 0;
    };

    const [year, setYear] = useState<string>(props.value
        ? new Date(props.value).getFullYear().toString()
        : props.value
            ? new Date(props.value).getFullYear().toString()
            : ""
    );
    const [month, setMonth] = useState<string>(props.value
        ? new Date(props.value).getMonth().toString()
        : props.value
            ? new Date(props.value).getMonth().toString()
            : ""
    );
    const [day, setDay] = useState<string>(props.value
        ? new Date(props.value).getDate().toString()
        : props.value
            ? new Date(props.value).getDate().toString()
            : ""
    );

    const getDateOrder = useMemo(() => {
        const sample = new Date(2000, 10, 22); // 22 Nov 2000 � unambiguous
        const parts = new Intl.DateTimeFormat(navigator.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).formatToParts(sample);

        return parts
            .filter(p => ['day', 'month', 'year'].includes(p.type))
            .map(p => p.type);
    }, [navigator.language]);

    const getYears = useMemo(() => {
        const min = props.minYear ?? new Date().getFullYear();
        const max = props.maxYear ?? new Date().getFullYear();

        if (max < min) return [];

        let current = max;
        const values: string[] = [];

        while (current >= min) {
            values.push(current.toString());
            current--;
        }

        return values;
    }, [props.minYear, props.maxYear]);

    const getMonths = useMemo(() => {
        const values: string[] = [];

        for (let i = 1; i <= 12; i++) {
            values.push(i.toString());
        }

        return values;
    }, []);

    const getDays = useMemo(() => {
        const values: string[] = [];

        for (let i = 1; i <= 31; i++) {
            values.push(i.toString());
        }

        return values;
    }, []);

    const showLabels = useMemo(() => {
        return (!!props.dayLabel || !!props.monthLabel || !!props.yearLabel);
    }, [props.dayLabel, props.monthLabel, props.yearLabel]);

    const renderYearControl = (): ReactNode => {
        return (
            <div className={styles.year}>
                {showLabels && <div className={styles.label}>{props.yearLabel ?? ""}</div>}
                <Select
                    id={`${props.id}-y`}
                    items={getYears}
                    descriptions={y => y}
                    values={y => y}
                    value={year}
                    onChange={e => setYear(e.target.value)}
                />
            </div>
        );
    };

    const renderMonthControl = (): ReactNode => {
        return (
            <div className={styles.month}>
                {showLabels && <div className={styles.label}>{props.monthLabel ?? ""}</div>}
                <Select
                    id={`${props.id}-m`}
                    items={getMonths}
                    descriptions={m => m}
                    values={m => m}
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                />
            </div>
        );
    };

    const renderDayControl = (): ReactNode => {
        return (
            <div className={styles.day}>
                {showLabels && <div className={styles.label}>{props.dayLabel ?? ""}</div>}
                <Select
                    id={`${props.id}-d`}
                    items={getDays}
                    descriptions={d => d}
                    values={d => d}
                    value={day}
                    onChange={e => setDay(e.target.value)}
                />
            </div>
        );
    };

    const renderControls = (): ReactNode => {
        return getDateOrder.map(o => {
            switch (o) {
                case "year":
                    return renderYearControl();
                case "month":
                    return renderMonthControl();
                case "day":
                    return renderDayControl();
                default:
                    return null;
            }

        });
    };

    useEffect(() => {
        if (!year || !month || !day) {
            props.onChange("");
            return;
        }

        const parsedYear = parseInt(year);
        const parsedMonth = parseInt(month) - 1; // Stupid JS dates start months at 0
        const parsedDay = parseInt(day);

        const date = new Date(parsedYear, parsedMonth, parsedDay);

        if (
            date.getFullYear() === parsedYear &&
            date.getMonth() === parsedMonth &&
            date.getDate() === parsedDay
        ) {

            const iso =
                parsedYear.toString().padStart(4, "0") + "-" +
                (parsedMonth + 1).toString().padStart(2, "0") + "-" +
                parsedDay.toString().padStart(2, "0");

            props.onChange(iso);
        }
        else {
            props.onChange("");
        }
    }, [year, month, day, props.onChange]);

    return (
        <div className={styles.container}>

            {!!props.label && (
                <div className={classNames([commonStyles.label, isErrorState() && commonStyles.error])}>
                    <label data-required={props.validations?.required} htmlFor={props.id}>{props.label}</label>
                </div>
            )}

            <div className={styles.parts}>
                {renderControls()}
            </div>

            {isErrorState() && (
                <div className={commonStyles.validation}>
                    {props.validator?.getErrors(props.id).map((message) => {
                        return <div key={message}>{message}</div>;
                    })}
                </div>
            )}
        </div>
    );
};